import {
  SubmenuController
} from "./chunk.6A33SDH7.js";
import {
  getText
} from "./chunk.3GLWK2TQ.js";
import {
  LocalizeController
} from "./chunk.ZPJRWEFM.js";
import {
  WebAwesomeElement,
  e,
  n,
  r,
  t
} from "./chunk.LVUMIIKQ.js";
import {
  ke
} from "./chunk.S3625EU6.js";
import {
  __decorateClass
} from "./chunk.SZE6ONST.js";

// src/components/menu-item/menu-item.css
var menu_item_default = ":host {\n  --background-color-hover: var(--wa-color-neutral-fill-normal);\n  --text-color-hover: var(--wa-color-neutral-on-normal);\n  --submenu-offset: -0.125rem;\n\n  display: block;\n  color: var(--wa-color-text-normal);\n  position: relative;\n  display: flex;\n  align-items: stretch;\n  font: inherit;\n  padding: var(--wa-space-xs) var(--wa-space-2xs);\n  line-height: var(--wa-line-height-condensed);\n  transition: fill var(--wa-transition-normal) var(--wa-transition-easing);\n  user-select: none;\n  -webkit-user-select: none;\n  white-space: nowrap;\n  cursor: pointer;\n}\n\n:host([inert]) {\n  display: none;\n}\n\n:host([disabled]) {\n  outline: none;\n  opacity: 0.5;\n  cursor: not-allowed;\n}\n\n:host([loading]) {\n  outline: none;\n  cursor: wait;\n}\n\n:host([loading]) *:not(wa-spinner) {\n  opacity: 0.5;\n}\n\n:host([loading]) wa-spinner {\n  --indicator-color: currentColor;\n  --track-width: 0.0625rem;\n  position: absolute;\n  font-size: 0.8em;\n  top: calc(50% - 0.5em);\n  left: 0.5rem;\n  opacity: 1;\n}\n\n.label {\n  flex: 1 1 auto;\n  display: inline-block;\n  text-overflow: ellipsis;\n  overflow: hidden;\n}\n\n.prefix {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n}\n\n.prefix::slotted(*) {\n  margin-inline-end: var(--wa-space-xs);\n}\n\n.suffix {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n}\n\n.suffix::slotted(*) {\n  margin-inline-start: var(--wa-space-xs);\n}\n\n/* Safe triangle */\n:host(:state(submenu-expanded))::after {\n  content: '';\n  position: fixed;\n  z-index: 899;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  clip-path: polygon(\n    var(--safe-triangle-cursor-x, 0) var(--safe-triangle-cursor-y, 0),\n    var(--safe-triangle-submenu-start-x, 0) var(--safe-triangle-submenu-start-y, 0),\n    var(--safe-triangle-submenu-end-x, 0) var(--safe-triangle-submenu-end-y, 0)\n  );\n}\n\n:host(:focus-visible) {\n  outline: none;\n}\n\n:host(:hover:not([aria-disabled='true'], :focus-visible)),\n:host(:state(submenu-expanded)) {\n  background-color: var(--background-color-hover);\n  color: var(--text-color-hover);\n}\n\n:host(:focus-visible) {\n  outline: var(--wa-focus-ring);\n  outline-offset: calc(-1 * var(--wa-focus-ring-width));\n  background: var(--background-color-hover);\n  color: var(--text-color-hover);\n  opacity: 1;\n}\n\n.check,\n.chevron {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-size: 0.875em;\n  width: var(--wa-space-xl);\n  visibility: hidden;\n}\n\n:host([checked]) .check,\n:host(:state(has-submenu)) .chevron {\n  visibility: visible;\n}\n\n/* Add elevation and z-index to submenus */\nwa-popup::part(popup) {\n  box-shadow: var(--wa-shadow-m);\n  z-index: 900;\n  margin-left: var(--submenu-offset);\n}\n\nwa-popup:dir(rtl)::part(popup) {\n  margin-left: calc(-1 * var(--submenu-offset));\n}\n\n@media (forced-colors: active) {\n  :host(:hover:not([aria-disabled='true'])),\n  :host(:focus-visible) {\n    outline: dashed 1px SelectedItem;\n    outline-offset: -1px;\n  }\n}\n\n::slotted(wa-menu) {\n  max-width: var(--auto-size-available-width) !important;\n  max-height: var(--auto-size-available-height) !important;\n}\n";

// src/components/menu-item/menu-item.ts
var WaMenuItem = class extends WebAwesomeElement {
  constructor() {
    super(...arguments);
    this.localize = new LocalizeController(this);
    this.type = "normal";
    this.checked = false;
    this.value = "";
    this.loading = false;
    this.disabled = false;
    this._label = "";
    this.defaultLabel = "";
    this.withSubmenu = false;
    this.submenuController = new SubmenuController(this);
    this.handleHostClick = (event) => {
      if (this.disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    this.handleMouseOver = (event) => {
      this.focus();
      event.stopPropagation();
    };
  }
  set label(value) {
    const oldValue = this._label;
    this._label = value || "";
    if (this._label !== oldValue) {
      this.requestUpdate("label", oldValue);
    }
  }
  get label() {
    if (this._label) {
      return this._label;
    }
    if (!this.defaultLabel) {
      this.updateDefaultLabel();
    }
    return this.defaultLabel;
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.handleHostClick);
    this.addEventListener("mouseover", this.handleMouseOver);
    this.updateDefaultLabel();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.handleHostClick);
    this.removeEventListener("mouseover", this.handleMouseOver);
  }
  firstUpdated(changedProperties) {
    if (this.isSubmenu()) {
      this.requestUpdate();
    }
    super.firstUpdated(changedProperties);
  }
  handleDefaultSlotChange() {
    let labelChanged = this.updateDefaultLabel();
    if (labelChanged) {
      this.dispatchEvent(new Event("slotchange", { bubbles: true, composed: false, cancelable: false }));
    }
    this.toggleCustomState("has-submenu", this.isSubmenu());
  }
  updated(changedProperties) {
    if (changedProperties.has("checked")) {
      if (this.checked && this.type !== "checkbox") {
        this.checked = false;
        return;
      }
      if (this.type === "checkbox") {
        this.setAttribute("aria-checked", this.checked ? "true" : "false");
      } else {
        this.removeAttribute("aria-checked");
      }
    }
    if (changedProperties.has("disabled")) {
      this.setAttribute("aria-disabled", this.disabled ? "true" : "false");
    }
    if (changedProperties.has("type")) {
      if (this.type === "checkbox") {
        this.setAttribute("role", "menuitemcheckbox");
        this.setAttribute("aria-checked", this.checked ? "true" : "false");
      } else {
        this.setAttribute("role", "menuitem");
        this.removeAttribute("aria-checked");
      }
    }
  }
  updateDefaultLabel() {
    let oldValue = this.defaultLabel;
    this.defaultLabel = getText(this).trim();
    let changed = this.defaultLabel !== oldValue;
    if (!this._label && changed) {
      this.requestUpdate("label", oldValue);
    }
    return changed;
  }
  /** Does this element have a submenu? */
  isSubmenu() {
    return this.hasUpdated ? this.querySelector(`:scope > [slot="submenu"]`) !== null : this.withSubmenu;
  }
  render() {
    const isRtl = this.hasUpdated ? this.localize.dir() === "rtl" : this.dir === "rtl";
    const isSubmenuExpanded = this.submenuController.isExpanded();
    this.toggleCustomState("submenu-expanded", isSubmenuExpanded);
    this.internals.ariaHasPopup = this.isSubmenu() + "";
    this.internals.ariaExpanded = isSubmenuExpanded + "";
    return ke`
      <slot name="checked-icon" part="checked-icon" class="check">
        <wa-icon name="check" library="system" variant="solid" aria-hidden="true"></wa-icon>
      </slot>

      <slot name="prefix" part="prefix" class="prefix"></slot>

      <slot part="label" class="label" @slotchange=${this.handleDefaultSlotChange}></slot>

      <slot name="suffix" part="suffix" class="suffix"></slot>

      <slot name="submenu-icon" part="submenu-icon" class="chevron">
        <wa-icon
          name=${isRtl ? "chevron-left" : "chevron-right"}
          library="system"
          variant="solid"
          aria-hidden="true"
        ></wa-icon>
      </slot>

      ${this.submenuController.renderSubmenu()} ${this.loading ? ke`<wa-spinner part="spinner"></wa-spinner>` : ""}
    `;
  }
};
WaMenuItem.shadowStyle = menu_item_default;
__decorateClass([
  e("slot:not([name])")
], WaMenuItem.prototype, "defaultSlot", 2);
__decorateClass([
  e(".menu-item")
], WaMenuItem.prototype, "menuItem", 2);
__decorateClass([
  n()
], WaMenuItem.prototype, "type", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], WaMenuItem.prototype, "checked", 2);
__decorateClass([
  n()
], WaMenuItem.prototype, "value", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], WaMenuItem.prototype, "loading", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], WaMenuItem.prototype, "disabled", 2);
__decorateClass([
  n()
], WaMenuItem.prototype, "label", 1);
__decorateClass([
  r()
], WaMenuItem.prototype, "defaultLabel", 2);
__decorateClass([
  n({ attribute: "with-submenu", type: Boolean })
], WaMenuItem.prototype, "withSubmenu", 2);
WaMenuItem = __decorateClass([
  t("wa-menu-item")
], WaMenuItem);

export {
  WaMenuItem
};
